import { z } from "zod";
import {
  Appointment,
  AppointmentStatus,
  AppointmentStatusSchema,
  CreateAppointmentDto,
  RecurrenceFrequencySchema,
  UpdateAppointmentDto,
} from "@myapp/contract";
import {
  APPOINTMENT_DEFAULT_DEPARTURE_MINUTES,
  APPOINTMENT_DEFAULT_TRAVEL_MINUTES,
  APPOINTMENT_DEFAULT_WORK_MINUTES,
} from "../../../entities/appointment";
import { fromDatetimeLocal, toDatetimeLocal } from "../../../shared/helpers/date";
import {
  durationsFromInstants,
  firstInstantOutOfOrder,
  instantsFromSlot,
  overrideFields,
  type ActualInstants,
  type ScheduleOverrides,
  type ScheduleSlot,
} from "../../../shared/lib/schedule";

/**
 * One form for create and edit. The two differ only in which mapper runs and whether `recurrence`
 * is offered — `POST /appointments` and `PATCH /appointments/:id` take the same business fields.
 *
 * THE TRAP THIS FILE EXISTS TO AVOID: `plannedStart` is the **work** start, and it round-trips
 * through `schedule.plannedWorkStartsAt` — never `schedule.startsAt`, which is the rendered block
 * and begins `plannedTravelMinutes` earlier. Reading the block start back into the form would
 * silently walk every appointment backwards by its travel time on each save.
 *
 * THE SECOND TRAP: the Ist fields are only ever sent for a completed appointment. Occupancy is
 * `COALESCE(actual, planned)` per component, so an Ist start on a still PLANNED appointment moves its
 * calendar block — with a 200 and no error anywhere.
 *
 * That gate reads the **form's** `status`, not the loaded appointment's, because the status is now
 * editable: a manager who picks "Abgeschlossen" gets the Ist-Zeiten fields in the same render and
 * submits status and actuals in one request. Reading the server's status instead would offer the
 * fields one save late, and send nothing the first time.
 *
 * THE THIRD TRAP, and what `toSlot` / `applySlot` exist for: occupancy is `COALESCE(actual, planned)`
 * per component, so a **completed** appointment is drawn from its Ist-Zeiten. The calendar block and
 * the five fields the board reads and writes therefore have to be the Ist ones for as long as the
 * form says the appointment is done — reading `plannedStart` there would make the block jump off the
 * position it was drawn at the moment the form opened, and writing it back would save a move that
 * never becomes visible. `hasActuals` decides the side in exactly one place, here.
 *
 * IST-ZEITEN ARE INSTANTS, NOT DURATIONS (Task 2040). The office edits the same four moments the
 * Handwerker's taps stamp — Anfahrt ab, Arbeit ab, Arbeit bis, Abfahrt bis — and the server derives
 * the Ist-Minuten from them. Nothing here can leave a completed Termin with an open phase any more.
 */

/**
 * Actuals are collected once the technician is done, so the form offers them exactly for the two
 * completed states. Single source of truth for both the fields and the update mapper.
 */
export function hasActuals(status?: AppointmentStatus): boolean {
  return status === "COMPLETED" || status === "COMPLETED_WITH_FOLLOW_UP";
}

const recurrenceFormSchema = z.object({
  frequency: RecurrenceFrequencySchema,
  until: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Format TT.MM.JJJJ"),
});

/**
 * German messages on every constraint, including the ones that are hard to reach.
 *
 * Zod's defaults are English ("Number must be greater than or equal to 0"), and this UI is not.
 * The duration fields rarely surface one — `FormInput` maps an empty box to `""` and `z.coerce`
 * turns that into `0` — but a pasted `-30` or `1.5` reaches them, and an untranslated sentence in
 * the middle of a German form is worse than the field it belongs to.
 */
const MINUTES = { invalid: "Ganze Minuten angeben", negative: "Darf nicht negativ sein" } as const;

/** What a mis-ordered instant is told, keyed by the field that came too early. */
export const INSTANT_ORDER_MESSAGES: Record<keyof ActualInstants, string> = {
  travelStartedAt: "Ungültiger Zeitpunkt",
  actualStart: "Muss nach „Anfahrt ab“ liegen",
  actualEnd: "Muss nach „Arbeit ab“ liegen",
  departureEndedAt: "Muss nach „Arbeit bis“ liegen",
};

export const appointmentFormSchema = z
  .object({
    workOrderId: z.string().min(1, "Pflichtfeld"),
    /** Stays nullable so the empty form has a valid default; emptiness is caught below. */
    assignedTechnicianId: z.number().int().positive("Pflichtfeld").nullable(),
    /** `<input type="datetime-local">` value — local wall-clock, no timezone. */
    plannedStart: z.string().min(1, "Pflichtfeld"),
    plannedWorkDuration: z.coerce.number().int(MINUTES.invalid).positive("Pflichtfeld"),
    plannedTravelDuration: z.coerce.number().int(MINUTES.invalid).min(0, MINUTES.negative),
    plannedDepartureDuration: z.coerce.number().int(MINUTES.invalid).min(0, MINUTES.negative),
    /**
     * Ist-Zeitpunkte, all `<input type="datetime-local">` values. Always present in the form state
     * so the inputs stay controlled, but only surfaced and only submitted for a completed
     * appointment — see `hasActuals`. `""` is "not recorded" and goes out as `null`.
     *
     * `actualStart` is the Ist **work** start, the twin of `plannedStart` and subject to the same
     * trap: it round-trips through `schedule.actualWorkStartsAt`, never through `startsAt`.
     * `actualEnd` is what every "läuft" reading hangs on — a completed Termin without one would
     * still render as in progress — which is why `superRefine` makes it a Pflichtfeld.
     */
    travelStartedAt: z.string(),
    actualStart: z.string(),
    actualEnd: z.string(),
    departureEndedAt: z.string(),
    description: z.string(),
    /**
     * Edit: any of the five states. Create: PLANNED or FOLLOW_UP only, which is how a manager books
     * a Folgetermin before the technician has asked for one — see `toCreateDto`.
     */
    status: AppointmentStatusSchema,
    isFullDay: z.boolean(),
    attachments: z.array(z.string()),
    /** `null` === one-time. Only offered on create; frequency is not patchable. */
    recurrence: recurrenceFormSchema.nullable(),
    /** Edit only, and only meaningful on an appointment that has a `seriesId`. */
    forFullSeries: z.boolean(),
  })
  .superRefine((data, ctx) => {
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: [path] });

    if (data.assignedTechnicianId == null) issue("assignedTechnicianId", "Pflichtfeld");
    if (data.recurrence && !fromDatetimeLocal(data.plannedStart)) {
      issue("plannedStart", "Ungültiges Datum");
    }

    // Ist-Zeiten: an Abschluss needs a start and an end, and the four must read as one session —
    // the server refuses anything else with 400, and a field error beats a toast.
    if (hasActuals(data.status)) {
      if (!fromDatetimeLocal(data.actualStart)) issue("actualStart", "Pflichtfeld");
      if (!fromDatetimeLocal(data.actualEnd)) issue("actualEnd", "Pflichtfeld");
      const outOfOrder = firstInstantOutOfOrder(data);
      if (outOfOrder) issue(outOfOrder, INSTANT_ORDER_MESSAGES[outOfOrder]);
    }
  });

export type AppointmentFormValues = z.infer<typeof appointmentFormSchema>;

export function getDefaultValues(workOrderId = ""): AppointmentFormValues {
  return {
    workOrderId,
    assignedTechnicianId: null,
    plannedStart: "",
    plannedWorkDuration: APPOINTMENT_DEFAULT_WORK_MINUTES,
    plannedTravelDuration: APPOINTMENT_DEFAULT_TRAVEL_MINUTES,
    plannedDepartureDuration: APPOINTMENT_DEFAULT_DEPARTURE_MINUTES,
    travelStartedAt: "",
    actualStart: "",
    actualEnd: "",
    departureEndedAt: "",
    description: "",
    // On create the modal replaces this with FOLLOW_UP once it knows the Auftrag is waiting for one.
    status: "PLANNED",
    isFullDay: false,
    attachments: [],
    recurrence: null,
    forFullSeries: false,
  };
}

/** Everything `toSlot` and `applySlot` read. `status` decides which side of the COALESCE they use. */
export type SlotValues = Pick<
  AppointmentFormValues,
  | "assignedTechnicianId"
  | "plannedStart"
  | "plannedTravelDuration"
  | "plannedWorkDuration"
  | "plannedDepartureDuration"
  | "travelStartedAt"
  | "actualStart"
  | "actualEnd"
  | "departureEndedAt"
> & { status?: AppointmentStatus };

/** The four Ist fields of a form, as the shared helpers read them. */
export function actualInstantsOf(values: SlotValues): ActualInstants {
  return {
    travelStartedAt: values.travelStartedAt,
    actualStart: values.actualStart,
    actualEnd: values.actualEnd,
    departureEndedAt: values.departureEndedAt,
  };
}

/**
 * The four scheduling values as a `ScheduleSlot` — **what the calendar draws** while the form is
 * open, which for a completed appointment is its Ist-Zeiten and otherwise its Plan-Zeiten.
 *
 * The returned keys are named after the planned fields because they are the RHF field names the
 * board never inspects; see the note on `ScheduleSlot` itself. What they carry is the side
 * `hasActuals` selects — on the Ist side, the durations are *derived* from the four instants exactly
 * as the server will derive them, with the planned Anfahrt/Abfahrt standing in for an instant that
 * was not recorded (the server's occupancy COALESCE does the same).
 *
 * `null` means "there is nothing to draw yet", not "clear the block": no Handwerker chosen, a start
 * still being typed, or a duration input the user has just emptied (`valueAsNumber` hands back
 * `NaN`). The board keeps showing the last good position in that case, which is why this must not
 * fall back to zeroes — a `NaN` smuggled through would place the block at 06:00.
 *
 * No mapping happens here: the start stays the datetime-local **work** start, exactly as the board's
 * `slotToDraft` expects it.
 */
export function toSlot(values: SlotValues): ScheduleSlot | null {
  if (values.assignedTechnicianId == null) return null;

  if (hasActuals(values.status)) {
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(values.actualStart)) return null;
    const durations = durationsFromInstants(actualInstantsOf(values), {
      travel: values.plannedTravelDuration,
      departure: values.plannedDepartureDuration,
    });
    if (!durations) return null;
    return {
      assignedTechnicianId: values.assignedTechnicianId,
      plannedStart: values.actualStart,
      plannedTravelDuration: durations.travel,
      plannedWorkDuration: durations.work,
      plannedDepartureDuration: durations.departure,
    };
  }

  const { plannedStart: start } = values;
  const travel = values.plannedTravelDuration;
  const work = values.plannedWorkDuration;
  const departure = values.plannedDepartureDuration;

  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(start)) return null;
  if (![travel, work, departure].every(Number.isFinite)) return null;

  return {
    assignedTechnicianId: values.assignedTechnicianId,
    plannedStart: start,
    plannedTravelDuration: travel,
    plannedWorkDuration: work,
    plannedDepartureDuration: departure,
  };
}

/**
 * The Ist fields a slot translates to — what the board's drag and the Plan→Ist copy write.
 * Datetime-local in, datetime-local out.
 */
export function slotToActualInstants(slot: ScheduleSlot): ActualInstants {
  return instantsFromSlot(slot.plannedStart, {
    travel: slot.plannedTravelDuration,
    work: slot.plannedWorkDuration,
    departure: slot.plannedDepartureDuration,
  });
}

/**
 * The inverse of `toSlot`: a slot chosen elsewhere — the board — written onto whichever side this
 * form's status puts it on.
 *
 * Used by the seeding `reset`, where the picked slot has to win over the loaded appointment. A plain
 * spread of the slot cannot do it any more: its keys are the *planned* field names, so on a
 * completed appointment it would fill the Plan-Zeiten with a position the block does not use and
 * leave the Ist-Zeiten — the ones actually drawn — untouched.
 */
export function applySlot(
  values: AppointmentFormValues,
  slot?: ScheduleSlot,
): AppointmentFormValues {
  if (!slot) return values;

  const scheduling = hasActuals(values.status)
    ? slotToActualInstants(slot)
    : {
        plannedStart: slot.plannedStart,
        plannedTravelDuration: slot.plannedTravelDuration,
        plannedWorkDuration: slot.plannedWorkDuration,
        plannedDepartureDuration: slot.plannedDepartureDuration,
      };

  return {
    ...values,
    assignedTechnicianId: slot.assignedTechnicianId,
    ...scheduling,
  };
}

/**
 * `sendStatus` is false while the Auftrag has not loaded: the form's status is then just the
 * `PLANNED` default, not a choice, and sending it would override the backend's own rule (an Auftrag
 * waiting for a Folgetermin makes the new Termin one). Omitted, the backend decides as before.
 */
export function toCreateDto(
  values: AppointmentFormValues,
  overrides: ScheduleOverrides = {},
  sendStatus = true,
): CreateAppointmentDto {
  return {
    workOrderId: values.workOrderId,
    // Non-null by the time this runs — `superRefine` blocks submit otherwise.
    assignedTechnicianId: values.assignedTechnicianId as number,
    plannedStart: fromDatetimeLocal(values.plannedStart) as string,
    plannedWorkDuration: values.plannedWorkDuration,
    plannedTravelDuration: values.plannedTravelDuration,
    plannedDepartureDuration: values.plannedDepartureDuration,
    attachments: values.attachments.length ? values.attachments : null,
    description: values.description || null,
    isFullDay: values.isFullDay,
    recurrence: values.recurrence ?? undefined,
    ...(sendStatus ? { status: values.status === "FOLLOW_UP" ? "FOLLOW_UP" : "PLANNED" } : {}),
    ...overrideFields(overrides),
  };
}

/**
 * `recurrence` is deliberately absent: `UpdateAppointmentDtoSchema` does not accept it. Changing a
 * frequency would mean re-reserving every future occurrence, so the API refuses — the horizon moves
 * through `PATCH /appointments/series/:seriesId` instead.
 *
 * With `forFullSeries`, `plannedStart` is treated by the backend as a **delta**: it moves this
 * appointment to the given time and shifts every later occurrence by the same offset. The client
 * still sends the plain absolute time — no offset math here.
 *
 * The four Ist-Zeitpunkte are omitted entirely unless the appointment is completed. The DTO is
 * `.partial()`, so omitting them leaves the stored actuals alone; sending them on a PLANNED
 * appointment would move its block, because occupancy prefers actuals over planned.
 *
 * `actualStart` and `actualEnd` are guaranteed by `superRefine`; the outer two go out as `null` when
 * empty **on purpose**: a hidden „Anfahrt ab" (no Anfahrt planned) must clear a stale instant, and
 * the server then derives an honest 0 for the Anfahrt minutes. Nothing here sends a duration.
 *
 * The planned keys keep being sent unchanged on a completed appointment. That is deliberate: they
 * carry what was *planned*, the drag never touched them, and re-sending the loaded values is what
 * preserves the Plan/Ist comparison.
 *
 * `status` is sent unconditionally, and the API ignores it when it matches what is stored. Setting
 * it to a completed state carries the same WorkOrder bump as `PATCH :id/complete` and requires a
 * manager role, so a 403 here is about the status, not the time.
 *
 * The overrides behave exactly as in `toCreateDto`: set only after the user confirmed the
 * matching dialog.
 */
export function toUpdateDto(
  values: AppointmentFormValues,
  options: ScheduleOverrides & { hasSeries: boolean },
): UpdateAppointmentDto {
  return {
    assignedTechnicianId: values.assignedTechnicianId as number,
    plannedStart: fromDatetimeLocal(values.plannedStart) as string,
    plannedWorkDuration: values.plannedWorkDuration,
    plannedTravelDuration: values.plannedTravelDuration,
    plannedDepartureDuration: values.plannedDepartureDuration,
    attachments: values.attachments.length ? values.attachments : null,
    description: values.description || null,
    isFullDay: values.isFullDay,
    status: values.status,
    ...(hasActuals(values.status)
      ? {
          travelStartedAt: fromDatetimeLocal(values.travelStartedAt),
          actualStart: fromDatetimeLocal(values.actualStart) as string,
          actualEnd: fromDatetimeLocal(values.actualEnd) as string,
          departureEndedAt: fromDatetimeLocal(values.departureEndedAt),
        }
      : {}),
    ...(options.hasSeries ? { forFullSeries: values.forFullSeries } : {}),
    ...overrideFields(options),
  };
}

export function fromAppointment(a: Appointment): AppointmentFormValues {
  const s = a.schedule;

  /**
   * Ist-Zeiten are nullable until the technician reports them. A completed Termin that was never
   * *started* has no Ist instants at all, and opening the form on four blanks would publish nothing
   * to the calendar — so what was planned stands in, laid out as instants, which is also the sensible
   * starting point for a manager correcting them. Stored instants always win; the stored minutes
   * decide the length of a phase whose instant is missing (a legacy row the office typed minutes on).
   */
  const plannedAsInstants = instantsFromSlot(
    toDatetimeLocal(s.actualWorkStartsAt ?? s.plannedWorkStartsAt),
    {
      travel: s.actualTravelMinutes ?? s.plannedTravelMinutes,
      work: s.actualWorkMinutes ?? s.plannedWorkMinutes,
      departure: s.actualDepartureMinutes ?? s.plannedDepartureMinutes,
    },
  );

  return {
    workOrderId: a.workOrderId,
    assignedTechnicianId: s.technicianId,
    // plannedWorkStartsAt, NOT startsAt — see the note at the top of this file.
    plannedStart: toDatetimeLocal(s.plannedWorkStartsAt),
    plannedWorkDuration: s.plannedWorkMinutes,
    plannedTravelDuration: s.plannedTravelMinutes,
    plannedDepartureDuration: s.plannedDepartureMinutes,
    travelStartedAt: toDatetimeLocal(s.travelStartedAt) || plannedAsInstants.travelStartedAt,
    actualStart: toDatetimeLocal(s.actualWorkStartsAt) || plannedAsInstants.actualStart,
    actualEnd: toDatetimeLocal(s.actualEndsAt) || plannedAsInstants.actualEnd,
    departureEndedAt: toDatetimeLocal(s.departureEndedAt) || plannedAsInstants.departureEndedAt,
    description: a.description ?? "",
    status: a.status,
    isFullDay: s.allDay,
    attachments: a.attachments ?? [],
    recurrence: null,
    forFullSeries: false,
  };
}
